import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  useRef,
} from 'react'
import { Helmet } from 'react-helmet'
import { useLocation, useParams, useRouteMatch } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import InputAdornment from '@material-ui/core/InputAdornment'
import IconButton from '@material-ui/core/IconButton'
import SearchOutlinedIcon from '@material-ui/icons/SearchOutlined'
import AddOutlinedIcon from '@material-ui/icons/AddOutlined'
import { Channel, ChannelList, useChatContext, Thread } from 'stream-chat-react'
import { EmojiPicker } from 'stream-chat-react/emojis'
import { withStreamClient } from 'lib/stream-messaging'
import { useDebounce, useIsMobile, useMobileHeight } from 'lib/hooks'
import ChannelPreview from './channel-preview'
import { ChannelInner, CustomAttachment, CustomMessage } from './channel-inner'
import NewConversationDialog from './new-conversation-dialog'
import EmptyChannel from './empty-channel'
import { AttachmentContextProvider } from './use-attachment-context'
import ConversationLoader from './components/conversation-loader'
import HiddenMessages from './hidden-messages'
import { PageHeader } from '../page-header'

const ChannelListHeader = React.memo(({ searchQuery, setSearchQuery }) => {
  return (
    <Box
      display="flex"
      p={2}
      alignItems="center"
      className="str-chat__channel-list-header"
    >
      <Box display="flex" flexGrow={1} mr={2} bgcolor="white" borderRadius={4}>
        <TextField
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          color="primary"
          variant="outlined"
          margin="none"
          placeholder="Search"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon color="primary" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <NewConversationDialog
        trigger={
          <IconButton color="primary">
            <AddOutlinedIcon />
          </IconButton>
        }
      />
    </Box>
  )
})

const Messenger = withStreamClient(() => {
  const { channelId: initialChannelId } = useParams()
  const { pathname } = useLocation()
  const { path } = useRouteMatch()
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [displayChannelList, setDisplayChannelList] = useState(false)
  const debouncedSearchQuery = useDebounce(searchQuery, 300)?.trim()
  const { channel, client, setActiveChannel } = useChatContext()
  const [hiddenChannels, setHiddenChannels] = useState([])
  const isMobile = useIsMobile()
  useMobileHeight()

  const MemoizedChannelListHeader = useCallback(
    () => (
      <ChannelListHeader
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />
    ),
    [searchQuery, setSearchQuery],
  )

  const newConversationDialogTrigger = isMobile ? (
    <IconButton color="primary">
      <AddOutlinedIcon />
    </IconButton>
  ) : (
    <Button variant="contained" color="primary">
      New Message
    </Button>
  )

  useEffect(() => {
    if (!displayChannelList) {
      const hasChannels = async () => {
        const channels = await client.queryChannels(filters, sort, { limit: 1 })
        setDisplayChannelList(channels.length > 0 || initialChannelId)
      }

      hasChannels()
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [channel])

  // if channelId parameter is provided, select the corresponding channel on load
  useEffect(() => {
    if (initialChannelId != null) {
      const updateActiveChannel = async () => {
        const filter = { id: initialChannelId }
        const sort = []
        const [ch] = await client.queryChannels(filter, sort, { limit: 1 })
        if (ch != null) {
          setActiveChannel(ch)
        }
      }

      updateActiveChannel()
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [])

  // get hidden channels
  useEffect(() => {
    const getHiddenChannels = async () => {
      const filters = {
        members: { $in: [client.user?.id] },
        frozen: false,
        hidden: true,
      }
      const sort = { last_message_at: -1 }
      const hiddenChannels = await client.queryChannels(filters, sort, {
        limit: 20,
      })
      setHiddenChannels(hiddenChannels)
    }

    getHiddenChannels()
    // Defer until after React re-renders the channel. Scroll the str-chat__list
    // container to the latest message. window.scrollTo is intentionally NOT used
    // here: scrolling the page triggers the sticky search header to overlap the
    // channel list, making top channels unreachable without the page scrollbar.
    setTimeout(() => {
      const hasMessages = channel?.state?.messages?.length > 0
      const streamList = document.querySelector('.str-chat__list')
      if (streamList) {
        streamList.scrollTop = hasMessages ? streamList.scrollHeight : 0
      }
      // Scroll the active channel preview into view within the channel list's
      // own scroll container. scroll-margin-top on the element (set in
      // style-overrides.css) offsets the scroll by the sticky header height.
      const activeChannel = document.querySelector(
        '.str-chat__channel-preview-messenger--active',
      )
      if (activeChannel) {
        activeChannel.scrollIntoView({ block: 'nearest' })
      }
    }, 0)
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [channel])

  // update browser url to match selected channel whenever it changes
  useEffect(() => {
    if (channel != null && path.includes(':channelId?')) {
      window.history.replaceState(
        null,
        null,
        path.replace(':channelId?', channel.id),
      )
    }
  }, [channel, path])

  const filters = {
    members: { $in: [client.user?.id] },
    frozen: false,
    hidden: false,
    ...(debouncedSearchQuery && {
      'member.user.name': { $autocomplete: debouncedSearchQuery },
    }),
    // prevent channels with no messages from showing up
    // last_message_at: { $exists: true }, doesn't work
    last_message_at: { $gt: '1970-01-01T00:00:00Z' }, // workaround
  }
  const sort = { last_message_at: -1 }
  const options = { limit: 20 }

  // Keep --page-header-bottom in sync with the page header's actual bottom edge so
  // the tablet channel-list panel sits precisely below it. displayChannelList is a
  // dep because the ref target only exists in the DOM after that flag turns true.
  const pageHeaderWrapperRef = useRef(null)
  useLayoutEffect(() => {
    const el = pageHeaderWrapperRef.current
    if (!el) return
    const update = () => {
      const bottom = el.getBoundingClientRect().bottom
      if (bottom <= 0) return
      document.documentElement.style.setProperty(
        '--page-header-bottom',
        `${bottom}px`,
      )
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayChannelList])

  // The conversation loader checks if we are creating a conversation and makes/finds
  // that channel, and otherwise returns the loading spinner.
  if (loading) {
    return <ConversationLoader setLoading={() => setLoading(false)} />
  }

  return displayChannelList ? (
    <Box
      display="flex"
      flexDirection="column"
      style={{ height: 'calc(var(--doc-height, 100vh) - 64px)' }}
    >
      <Helmet>
        <script
          type="text/javascript"
          src="https://unpkg.com/mediainfo.js"
        ></script>
      </Helmet>
      <div ref={pageHeaderWrapperRef}>
        <PageHeader
          title="Messaging"
          style={{
            position: 'sticky',
            top: '56px',
            zIndex: 10,
            backgroundColor: 'white',
          }}
          actions={
            <>
              {hiddenChannels.length > 0 && (
                <HiddenMessages
                  trigger={
                    <Button
                      variant={isMobile ? 'text' : 'outlined'}
                      style={{ marginRight: '12px' }}
                    >
                      Hidden Messages
                    </Button>
                  }
                  hiddenChannels={hiddenChannels}
                />
              )}
              <NewConversationDialog trigger={newConversationDialogTrigger} />
            </>
          }
        />
      </div>
      <div className="str-chat__layout" style={{ flex: 1, minHeight: 0 }}>
        <ChannelList
          filters={filters}
          sort={sort}
          options={options}
          showChannelSearch
          Preview={ChannelPreview}
          ChannelSearch={MemoizedChannelListHeader}
          setActiveChannelOnMount={
            !initialChannelId && !pathname.includes('/new-conversation')
          }
        />
        {channel && (
          <Channel
            channel={channel}
            Message={CustomMessage}
            Attachment={CustomAttachment}
            EmojiPicker={EmojiPicker}
          >
            <AttachmentContextProvider>
              <ChannelInner />
              <Thread />
            </AttachmentContextProvider>
          </Channel>
        )}
      </div>
    </Box>
  ) : (
    <EmptyChannel />
  )
})

export default Messenger
